import request from 'superagent';
import noCache from 'superagent-no-cache';
import {
  getRefreshToken,
  getAccessToken,
  saveTokenInfo,
  logoutActiveSessionWithoutToken,
  setConfirmationActionType,
  setSecureUserPhone,
  setLastUserActivityDateTime,
  resetLastUserActivityDateTime,
  setAvailableConfirmationTypes
} from './authUtils';
import { errorMapper } from './Error';
import { BACKEND_MODULE_TYPES, TWO_FACTOR_AUTH_TYPES } from 'components/common/constants';

const requestAgent = request.agent().use(noCache);

const {
  REACT_APP_API_URL_PATH_PREFIX,
  REACT_APP_API_HOST_WITH_PORT,
  REACT_APP_OAUTH_PATH,
  REACT_APP_OAUTH_URL_PATH_PREFIX,
  REACT_APP_OAUTH2_PATH,
  REACT_APP_OAUTH2_URL_PATH_PREFIX,
  REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
  REACT_APP_MESSAGER_API_URL_PATH_PREFIX
} = process.env;

// Fallback to old OAuth path if OAuth2 is not configured
const OAUTH2_PATH = REACT_APP_OAUTH2_PATH || REACT_APP_OAUTH_PATH;
const OAUTH2_URL_PREFIX = REACT_APP_OAUTH2_URL_PATH_PREFIX || REACT_APP_OAUTH_URL_PATH_PREFIX;

const TRANSPORT_DELAY = 20; // max seconds client waits for server response.
let isRefreshingToken = false;

/*
 "refreshTokenAwaiter" is a method that is used to create an artificial delay for any server calls if a request
 for refreshing the access token has been sent and a response with a new access token is expected.
 The delay step in this method is 100ms.
 The maximum number of iterations is 200, which is equal to the value of TRANSPORT_DELAY = 20sec.
 This method is handled using the "isRefreshingToken" variable.
 */
const refreshTokenAwaiter = () => {
  return new Promise((resolve) => {
    let count = 0;
    const interval = setInterval(() => {
      if (!isRefreshingToken) {
        clearInterval(interval);
        resolve();

        return;
      }
      count++;
      if (count > 200) {
        // 200 * 100ms (timeout) = 20 sec (TRANSPORT_DELAY)
        clearInterval(interval);
        resolve(errorMapper('invalid_refresh_token', {}, {}));
      }
    }, 100);
  });
};

// Common method! We need to use it for all API-calls
export const api = async (args) => {
  const {
    path: urlPathPostfix,
    method = 'post',
    query = {},
    data = {},
    pathHost = REACT_APP_API_HOST_WITH_PORT,
    pathPrefix = REACT_APP_API_URL_PATH_PREFIX,
    isRequestWithAuth = true,
    isRequestWithBasicAuth = false,
    clientSecret = null,
    isOAuth2Form = false,
    securityCode,
    returnFullResponse = false,
    responseType = null,
    captcha = null
  } = args;

  if (isRefreshingToken) {
    await refreshTokenAwaiter();
  }

  // eslint-disable-next-line no-console
  console.debug(
    '### API',
    method.toUpperCase(),
    'REQUEST',
    urlPathPostfix,
    ...((method === 'post' && ['\n', data]) ||
      (method === 'get' && query && Object.keys(query).length && ['\n', query]) ||
      [])
  );

  const req = requestAgent[method](pathHost + pathPrefix + urlPathPostfix);

  if (isOAuth2Form) {
    // Set Content-Type for OAuth2
    req.type('form'); // This sets Content-Type: application/x-www-form-urlencoded
  }

  if (isRequestWithAuth) {
    req.auth(getAccessToken(), { type: 'bearer' });
  }

  if (isRequestWithBasicAuth) {
    // Use clientSecret if provided, otherwise use empty string
    const secret = clientSecret !== null ? clientSecret : '';
    req.auth('web', secret);
  }

  if (responseType) {
    req.responseType(responseType);
  }

  if (query && Object.keys(query).length !== 0) {
    req.query(query);
  }

  if (method === 'post') {
    req.send(data);
  }

  if (securityCode) {
    req.set('Confirmation-Code', securityCode);
  }

  if (captcha) {
    req.set('Captcha', captcha);
  }

  setLastUserActivityDateTime();

  try {
    const res = await req; // XXX: `body` is `null` for 204 response.
    // eslint-disable-next-line no-console
    // console.log('RES api:', res);
    // eslint-disable-next-line no-console
    // console.debug('### API', method.toUpperCase(), 'RESPONSE', res?.body);
    return returnFullResponse ? res : res.body;
  } catch (error) {
    // eslint-disable-next-line no-console
    // console.debug('### API', method.toUpperCase(), 'ERROR', error);
    return errorMapper(error, {}, { path: urlPathPostfix, pathPrefix });
  }
};

/* █████████████████████ *\
 * ███ OAUTH-related ███ *
\* █████████████████████ */

let tokenTimeoutId;

const tokenize =
  (data, isOAuth2 = true) =>
  async () => {
    if (isRefreshingToken) return;

    try {
      isRefreshingToken = true;

      // Use new OAuth2 endpoint
      const tokenPath = isOAuth2 ? REACT_APP_OAUTH2_PATH : REACT_APP_OAUTH_PATH;
      const endpoint = '/token';

      const request = requestAgent.post(tokenPath + endpoint);

      // Add Basic auth for OAuth2 with client credentials
      if (isOAuth2) {
        // Set Content-Type for OAuth2
        request.type('form'); // This sets Content-Type: application/x-www-form-urlencoded
        // Get client secret from env or use empty string for development
        const clientSecret = process.env.REACT_APP_OAUTH2_CLIENT_SECRET || '';
        request.auth('web', clientSecret);
      } else {
        request.auth('web', '');
      }

      let response;
      try {
        response = await request.send(data);
      } catch (error) {
        console.error('OAuth2 token request failed:', {
          status: error.status,
          message: error.message,
          response: error.response?.body,
          text: error.response?.text
        });
        throw error;
      }

      const { body } = response;

      // Handle new OAuth2 response format
      const {
        access_token: accessToken,
        refresh_token: refreshToken,
        token_type: tokenType,
        expires_in: expiresIn,
        scope,
        confirmation,
        available_confirmation_types: availableConfirmationTypes,
        // Legacy fields for backward compatibility
        phone,
        confirmation_type: confirmationType,
        confirmation_id: confirmationId
      } = body;

      saveTokenInfo({ accessToken, refreshToken, tokenType, accessExpiresOn: Date.now() + expiresIn * 1000, scope });
      setTokenRefresh(expiresIn);

      // Handle new OAuth2 confirmation format
      if (confirmation) {
        const { confirmation_id, confirmation_type, expires_at } = confirmation;
        setConfirmationActionType(confirmation_type);
        if (availableConfirmationTypes) {
          setAvailableConfirmationTypes(availableConfirmationTypes);
        }
        return {
          confirmationType: confirmation_type,
          confirmationId: confirmation_id,
          expiresAt: expires_at,
          availableConfirmationTypes,
          scope
        };
      }

      // Legacy support
      if (confirmationType) {
        setConfirmationActionType(confirmationType);
      }
      if (availableConfirmationTypes) {
        setAvailableConfirmationTypes(availableConfirmationTypes);
      }
      if (phone) {
        setSecureUserPhone(phone);
      }

      return { confirmationType, phone, confirmationId, scope };
    } catch (error) {
      return errorMapper(error, {}, {});
    } finally {
      isRefreshingToken = false;
    }
  };

/**
 * @param {number} expiresIn - number of seconds after which access_token expires.
 */
export const setTokenRefresh = (expiresIn) => {
  clearTimeout(tokenTimeoutId);
  tokenTimeoutId = setTimeout(
    tokenize(
      {
        grant_type: 'refresh_token',
        refresh_token: getRefreshToken()
      },
      true
    ), // Use OAuth2
    (expiresIn - TRANSPORT_DELAY) * 1000
  );
};

export const loginUser = ({ phone, password, username }) => {
  // Prepare form data for OAuth2
  const formData = {
    grant_type: 'password',
    username: username || phone, // OAuth2 uses username instead of phone
    password
  };

  // Add device_id only if needed (optional)
  // You can uncomment this if backend requires it
  formData.device_id = window.navigator.userAgent;

  return tokenize(formData, true)(); // Use OAuth2
};

// New OAuth2 confirmation confirm endpoint
export const loginPin = async (code, confirmationId) => {
  try {
    const response = await requestAgent
      .post(`${OAUTH2_PATH}/confirmation/confirm`)
      .auth(getAccessToken(), { type: 'bearer' })
      .send({ code });

    const {
      access_token: accessToken,
      refresh_token: refreshToken,
      token_type: tokenType,
      expires_in: expiresIn,
      access_token_expires_at: accessTokenExpiresAt,
      refresh_token_expires_at: refreshTokenExpiresAt,
      scope
    } = response.body;

    saveTokenInfo({
      accessToken,
      refreshToken,
      tokenType,
      accessExpiresOn: Date.now() + expiresIn * 1000,
      scope
    });
    setTokenRefresh(expiresIn);

    return {
      success: true,
      accessTokenExpiresAt,
      refreshTokenExpiresAt,
      scope
    };
  } catch (error) {
    return errorMapper(error, {}, {});
  }
};

export const invalidateToken = () =>
  api({
    path: '/revoke',
    pathPrefix: REACT_APP_OAUTH2_URL_PATH_PREFIX,
    isRequestWithAuth: false,
    isRequestWithBasicAuth: true,
    isOAuth2Form: true,
    clientSecret: process.env.REACT_APP_OAUTH2_CLIENT_SECRET || '',
    data: { token: getAccessToken() }, // OAuth2 uses POST body instead of query
    method: 'post'
  });

export const runLogout = async () => {
  await invalidateToken();
  resetLastUserActivityDateTime();
  clearTimeout(tokenTimeoutId);
  logoutActiveSessionWithoutToken();
};

export const enableGoogle2FARequest = async () => {
  const res = await api({
    path: '/user/2fa/google-auth/activate',
    method: 'get',
    pathPrefix: REACT_APP_API_URL_PATH_PREFIX
  });

  return { ...res };
};

export const reactivateGoogle2FARequest = async () => {
  const res = await api({
    path: '/user/2fa/google-auth/reactivate',
    method: 'get',
    pathPrefix: REACT_APP_API_URL_PATH_PREFIX
  });

  return { ...res };
};

export const confirmGoogle2FARequest = async (data) => {
  const res = await api({
    path: '/user/2fa/google-auth/activate/confirm',
    pathPrefix: REACT_APP_API_URL_PATH_PREFIX,
    data
  });

  return { ...res };
};

export const disableGoogle2FARequest = async () => {
  const res = await api({
    path: '/user/2fa/google-auth/delete',
    pathPrefix: REACT_APP_API_URL_PATH_PREFIX,
    method: 'get'
  });

  return { ...res };
};

export const activateWhatsApp2FARequest = async () => {
  const res = await api({
    path: '/user/2fa/whatsapp/activate',
    method: 'get',
    pathPrefix: REACT_APP_API_URL_PATH_PREFIX
  });

  return { ...res };
};

export const activateSms2FARequest = async () => {
  const res = await api({
    path: '/user/2fa/sms/activate',
    method: 'get',
    pathPrefix: REACT_APP_API_URL_PATH_PREFIX
  });

  return { ...res };
};

// New OAuth2 push notification confirmation endpoints
export const confirmPushNotification = async (confirmationId, code) => {
  return await api({
    path: `/confirmation/${confirmationId}/push-notification/confirm`,
    method: 'post',
    data: { code },
    pathPrefix: OAUTH2_URL_PREFIX,
    isRequestWithAuth: true
  });
};

export const rejectPushNotification = async (confirmationId) => {
  return await api({
    path: `/confirmation/${confirmationId}/push-notification/reject`,
    method: 'get',
    pathPrefix: OAUTH2_URL_PREFIX,
    isRequestWithAuth: true
  });
};

// New OAuth2 force confirmation type change
export const forceChangeConfirmationTypeRequest = async (confirmationId, twoFactorAuthType, module) => {
  let res;

  if (module === BACKEND_MODULE_TYPES.OAUTH) {
    res = await api({
      path: `/confirmation/force/${twoFactorAuthType}`,
      method: 'get',
      pathPrefix: OAUTH2_URL_PREFIX,
      isRequestWithAuth: true
    });
  } else {
    res = await api({
      path: `/confirmation/${confirmationId}/force/${twoFactorAuthType}`,
      method: 'get',
      pathPrefix:
        module === BACKEND_MODULE_TYPES.API
          ? REACT_APP_PAYMENT_API_URL_PATH_PREFIX
          : module === BACKEND_MODULE_TYPES.PAYMENT
            ? REACT_APP_PAYMENT_API_URL_PATH_PREFIX
            : module === BACKEND_MODULE_TYPES.MESSENGER
              ? REACT_APP_MESSAGER_API_URL_PATH_PREFIX
              : REACT_APP_API_URL_PATH_PREFIX
    });
  }
  return { ...res };
};

// Check OAuth2 confirmation status using /confirm endpoint
export const checkOauthPushConfirmationStateRequest = async (confirmationId, module) => {
  return await api({
    path: `/confirmation/${confirmationId}/confirm`,
    method: 'get',
    isRequestWithAuth: true,
    pathPrefix: OAUTH2_URL_PREFIX
  });
};

export const checkPushConfirmationStateRequest = async (confirmationId, module) => {
  return await api({
    path: `/confirmation/${confirmationId}/confirm`,
    method: 'get',
    isRequestWithAuth: true, // OAuth2 requires auth for all confirmation endpoints
    pathPrefix:
      module === BACKEND_MODULE_TYPES.OAUTH
        ? OAUTH2_URL_PREFIX
        : module === BACKEND_MODULE_TYPES.API
          ? REACT_APP_API_URL_PATH_PREFIX
          : module === BACKEND_MODULE_TYPES.PAYMENT
            ? REACT_APP_PAYMENT_API_URL_PATH_PREFIX
            : module === BACKEND_MODULE_TYPES.MESSENGER
              ? REACT_APP_MESSAGER_API_URL_PATH_PREFIX
              : REACT_APP_API_URL_PATH_PREFIX
  });
};

/* ████████████████████████████ *\
 * ███ REGISTRATION-related ███ *
\* ████████████████████████████ */

let registrationResponseId = null;

const register = async (urlPathPostfix, data, captcha = null) => {
  try {
    const { body: res } = await requestAgent
      .post(REACT_APP_API_HOST_WITH_PORT + REACT_APP_API_URL_PATH_PREFIX + urlPathPostfix)
      .set('Captcha', captcha)
      .auth('web', '')
      .send(data);

    registrationResponseId = res?.response_id;

    if (res?.user_id) {
      return { response_id: res?.response_id, user_id: res?.user_id };
    } else if (res?.token && res?.user) {
      saveTokenInfo({
        accessToken: res?.token.access_token,
        refreshToken: res?.token.refresh_token,
        tokenType: res?.token.token_type,
        accessExpiresOn: Date.now() + res?.token.expires_in * 1000,
        scope: res?.token?.scope
      });
      setTokenRefresh(res?.token.expires_in);
      setConfirmationActionType(res?.token.two_factor_auth_type);
      setSecureUserPhone(res?.user.phone);

      return { token: res?.token, user: res?.user };
    } else if (res?.token_type === 'PRIVATE' && res?.access_token && res?.confirmation_id) {
      saveTokenInfo({
        accessToken: res?.access_token,
        refreshToken: res?.refresh_token,
        tokenType: res?.token_type,
        accessExpiresOn: Date.now() + res?.expires_in * 1000,
        scope: res?.token?.scope
      });

      if (res?.confirmation_type) {
        setConfirmationActionType(res?.confirmation_type);
      } else {
        setConfirmationActionType(res?.two_factor_auth_type);
      }

      if (res?.available_confirmation_types) {
        setAvailableConfirmationTypes(res?.available_confirmation_types);
      }

      return { confirmationId: res?.confirmation_id };
    } else if (res?.delivery_type) {
      setConfirmationActionType(res?.delivery_type);

      return { response_id: res?.response_id, delivery_type: res?.delivery_type };
    } else {
      return { response_id: res?.response_id };
    }
  } catch (error) {
    return errorMapper(error, {}, {});
  }
};

export const createUser = (data, captcha) => register('/registration/user', data, captcha);

export const confirmSmsCode = (code, utmParams) =>
  register('/registration/user/phone/confirm', {
    code,
    response_id: registrationResponseId,
    utm_tags: utmParams
  });

export const resendPhoneRegistration = () =>
  register('/registration/user/phone/resend_code', {
    response_id: registrationResponseId
  });

export const createAccount = async (data, captcha) => {
  return api({
    path: '/account/create',
    data,
    captcha
  });
};

export const confirmAccountEmail = (code, id) => {
  return api({
    path: '/account/create/email_confirmation/code',
    data: {
      response_id: id,
      code: code
    }
  });
};

export const resendCodeAccountEmail = (id) => {
  return api({
    path: '/api/account/create/email_confirmation/resend_code',
    data: { response_id: id }
  });
};

/* ████████████████ *\
 * ███ USER-API ███ *
\* ████████████████ */

export const getUserInformation = async () => {
  return api({
    path: '/user',
    method: 'get'
  });
};

export const getUserInfoByCustomerNumber = async (customerNumber) => {
  return api({
    path: `/account/${customerNumber}`,
    method: 'get'
  });
};

/* ████████████████████ *\
 * ███ ACCOUNTS-API ███ *
\* ████████████████████ */

export const getAccounts = async (customerNumber) => {
  return await api({
    path: `/account/${customerNumber}/wallet`,
    method: 'get',
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX
  });
};

export const downloadAccountDetailsPdfRequest = async (customerNumber, accountNumber) => {
  const response = await api({
    path: `/account/${customerNumber}/wallet/${accountNumber}/opening-letter`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    method: 'get',
    responseType: 'blob',
    isRequestWithAuth: true,
    isRequestWithBasicAuth: false,
    returnFullResponse: true
  });

  downloadFile(response);
};

/* █████████████████████████ *\
 * ███ VERIFICATION CODE ███ *
\* █████████████████████████ */

export const generateSecurityCode = async (module) => {
  const res = await api({
    path: '/confirmation/generate',
    method: 'get',
    pathPrefix:
      module === BACKEND_MODULE_TYPES.API
        ? REACT_APP_PAYMENT_API_URL_PATH_PREFIX
        : module === BACKEND_MODULE_TYPES.PAYMENT
          ? REACT_APP_PAYMENT_API_URL_PATH_PREFIX
          : module === BACKEND_MODULE_TYPES.MESSENGER
            ? REACT_APP_MESSAGER_API_URL_PATH_PREFIX
            : REACT_APP_OAUTH_URL_PATH_PREFIX
  });

  return { ...res };
};

export const resendSecurityCode = (module, confirmationId) => {
  // For OAuth module, use new OAuth2 endpoint
  if (module === BACKEND_MODULE_TYPES.OAUTH) {
    return api({
      path: '/confirmation/resend',
      method: 'get',
      pathPrefix: OAUTH2_URL_PREFIX,
      isRequestWithAuth: true
    });
  }

  // Legacy API for other modules
  return api({
    path: `/confirmation/${confirmationId}/resend`,
    method: 'get',
    pathPrefix:
      module === BACKEND_MODULE_TYPES.API
        ? REACT_APP_PAYMENT_API_URL_PATH_PREFIX
        : module === BACKEND_MODULE_TYPES.PAYMENT
          ? REACT_APP_PAYMENT_API_URL_PATH_PREFIX
          : module === BACKEND_MODULE_TYPES.MESSENGER
            ? REACT_APP_MESSAGER_API_URL_PATH_PREFIX
            : REACT_APP_OAUTH_URL_PATH_PREFIX
  });
};

/* ███████████████████████████*\
 * ███ RESET USER PASSWORD ███ *
\* ███████████████████████████ */

export const passwordReset = (data) =>
  api({
    path: '/user/change_password',
    data
  });

/* █████████████████████████ *\
 * ███ PASSWORD RECOVERY ███ *
\* █████████████████████████ */

export const passwordRecoveryPhone = (phone) => {
  return api({
    path: '/password_recovery/request',
    isRequestWithAuth: false,
    data: { phone: phone }
  });
};

export const passwordRecoveryPhoneConfirm = (id, code) => {
  return api({
    path: '/password_recovery/phone/confirm',
    isRequestWithAuth: false,
    data: { response_id: id, code: code }
  });
};

export const passwordRecoveryPhoneResend = (id) => {
  return api({
    path: '/password_recovery/phone/resend',
    isRequestWithAuth: false,
    data: { response_id: id }
  });
};

export const passwordRecoveryEmailCheck = (id, email) => {
  return api({
    path: '/password_recovery/email',
    isRequestWithAuth: false,
    data: { response_id: id, email: email }
  });
};

export const passwordRecoveryEmailConfirm = (id, code) => {
  return api({
    path: '/password_recovery/email/confirm',
    isRequestWithAuth: false,
    data: { response_id: id, code: code }
  });
};

export const passwordRecoveryEmailResend = (id) => {
  return api({
    path: '/password_recovery/email/resend',
    isRequestWithAuth: false,
    data: { response_id: id }
  });
};

export const changePasswordUser = (data) =>
  api({
    path: '/password_recovery/set_password',
    isRequestWithAuth: false,
    data
  });

export const passwordRecoveryRequest = (data) =>
  api({
    path: '/password_recovery',
    isRequestWithAuth: false,
    data
  });

export const generatePasswordRecoverySecurityCode = (responseId) =>
  api({
    path: '/password_recovery/request_confirmation',
    isRequestWithAuth: false,
    data: { response_id: responseId }
  });

export const fetchRecoveryLink = (tokenId) =>
  api({
    path: `/password_recovery/email/confirm/${tokenId}`,
    method: 'get',
    isRequestWithAuth: false
  });

/* ████████████████████████ *\
 * ███ TRANSACTIONS-API ███ *
\* ████████████████████████ */

export const getTransactionList = (customerNumber, filter = {}) =>
  api({
    path: `/account/${customerNumber}/transaction`,
    method: 'get',
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    query: filter
  });

export const getDocumentsVerificationTransactionList = (customerNumber) =>
  api({
    path: `/account/${customerNumber}/transaction/documents_processing`,
    method: 'get',
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX
  });

const downloadFile = (response, isPreview = false, filename) => {
  if (!filename && response.headers['content-disposition']) {
    const parts = response.headers['content-disposition'].split('filename=');
    if (parts.length === 2) {
      // eslint-disable-next-line no-param-reassign
      filename = parts[1].replace(/"/g, '');
    }
  }

  const url = URL.createObjectURL(response.body);

  if (isPreview) {
    return url;
  }

  const downloadLink = document.createElement('a');
  downloadLink.style.display = 'none';
  downloadLink.setAttribute('download', filename || 'downloadedFile');
  downloadLink.href = url;
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
};

export const exportCurrentPDF = async (customerNumber, transactionNumber) => {
  const response = await api({
    path: `/account/${customerNumber}/transaction/${transactionNumber}/report`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    method: 'get',
    responseType: 'blob',
    returnFullResponse: true
  });

  downloadFile(response);
};

export const exportPDFList = async (customerNumber, query) => {
  const response = await api({
    path: `/account/${customerNumber}/wallet/review`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    method: 'get',
    query,
    responseType: 'blob',
    returnFullResponse: true
  });

  downloadFile(response);
};

export const exportTransactionListCSV = async (customerNumber, query) => {
  const response = await api({
    path: `/account/${customerNumber}/transaction/export/csv`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    method: 'get',
    query,
    responseType: 'blob',
    returnFullResponse: true
  });

  downloadFile(response);
};

export const uploadDocumentsToTransaction = async (customerNumber, transactionNumber, file) => {
  return api({
    path: `/account/${customerNumber}/document/${transactionNumber}`,
    data: file,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX
  });
};

/* ███████████████████████████████ *\
 * ███ PAYMENT-OBLIGATIONS-API ███ *
\* ███████████████████████████████ */

export const getPaymentObligationsList = (customerNumber, filters = {}) =>
  api({
    path: `/account/${customerNumber}/payment-obligation/list`,
    method: 'get',
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    query: filters
  });

/* ██████████████████████████████████ *\
 * ███ APPLICATION-STATE_FROM-API ███ *
\* ██████████████████████████████████*/

export const getApplicationState = () =>
  api({
    path: '/application/state',
    method: 'get'
  });

export const getApplicationConstantsList = () =>
  api({
    path: '/application-constants/list',
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    method: 'get'
  });

/* ███████████████████ *\
 * ███ PAYMENT-API ███ *
\* ███████████████████ */

export const resendConfirmationCode = async (uid) =>
  await api({
    path: `/confirmation/${uid}/resend`,
    method: 'get',
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX
  });

export const calculatePaymentCommissionRequest = async (customerNumber, data) =>
  await api({
    path: `/account/${customerNumber}/transaction/commission/calculate`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    data
  });

export const transactionPaymentCheck = async (customerNumber, data) => {
  const res = await api({
    path: `/account/${customerNumber}/transaction/check`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    data
  });

  return {
    ...res,
    comment: data.comment
  };
};

export const transactionPaymentCreate = async (customerNumber, data) => {
  const res = await api({
    path: `/account/${customerNumber}/transaction/create`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    data
  });

  return { ...res };
};

export const transactionPaymentConfirm = async (customerNumber, data) => {
  const res = await api({
    path: `/account/${customerNumber}/transaction/confirm`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    data
  });

  return {
    ...res,
    comment: data.comment
  };
};

export const customerInternalTransactionCheck = async (customerNumber, data) =>
  await api({
    path: `/account/${customerNumber}/transaction/account-internal/check`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    data
  });

export const customerInternalTransactionCreate = async (customerNumber, data) =>
  await api({
    path: `/account/${customerNumber}/transaction/account-internal/create`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    data
  });

export const customerInternalTransactionConfirm = async (customerNumber, data) =>
  await api({
    path: `/account/${customerNumber}/transaction/account-internal/confirm`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    data
  });

export const uploadDocuments = (customerNumber, file) => {
  return api({
    path: `/account/${customerNumber}/document`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    data: file
  });
};

export const validateRecipientIbanOrCryptoWalletAddressRequest = async (customerNumber, data) => {
  return await api({
    path: `/account/${customerNumber}/transaction/validate-recipient`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    method: 'post',
    data
  });
};

/* ██████████████████████████ *\
 * ███ PAYMENT-CRYPTO-API ███ *
\* ██████████████████████████ */

export const transactionCryptoCheck = async (customerNumber, data) => {
  const res = await api({
    path: `/account/${customerNumber}/transaction/crypto/check`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    data
  });

  return {
    ...res,
    comment: data.comment
  };
};

export const transactionCryptoCreate = async (customerNumber, data) => {
  const res = await api({
    path: `/account/${customerNumber}/transaction/crypto/create`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    data
  });

  return { ...res };
};

export const transactionCryptoConfirm = async (customerNumber, data) => {
  const res = await api({
    path: `/account/${customerNumber}/transaction/crypto/confirm`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    data
  });

  return {
    ...res,
    comment: data.comment
  };
};

/* ████████████████████████ *\
 * ███ VERIFICATION-API ███ *
\* ████████████████████████ */

export const beginVerificationRequest = (customerNumber, data) => {
  return api({
    path: `/account/${customerNumber}/verification/begin`,
    data
  });
};

/* ████████████████ *\
 * ███ FEES-API ███ *
\* ████████████████ */

export const getTariffs = async (customerNumber, accountNumber, transactional = '') => {
  return api({
    // eslint-disable-next-line max-len
    path: `${
      transactional
        ? `/account/${customerNumber}/tariffs?wallet_number=${accountNumber}&transactional=${transactional}`
        : `/account/${customerNumber}/tariffs?&transactional=${transactional}`
    }`,
    method: 'get'
  });
};

export const setUnderReviewStatus = (customerNumber) => {
  return api({ path: `/account/${customerNumber}/aml/under_review` });
};

/* █████████████████████████████ *\
 * ███ CURRENCY-EXCHANGE-API ███ *
\* █████████████████████████████ */

export const createExchangeQuoteRequest = (customerNumber, data) =>
  api({
    path: `/account/${customerNumber}/exchange/quote/create`,
    data,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX
  });

export const calculateExchangeDataRequest = (customerNumber, data) =>
  api({
    path: `/account/${customerNumber}/exchange/calculate`,
    data,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX
  });

export const createExchangeTransferRequest = (customerNumber, data) =>
  api({
    path: `/account/${customerNumber}/exchange/create`,
    data,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX
  });

export const confirmExchangeTransferRequest = (customerNumber, data) =>
  api({
    path: `/account/${customerNumber}/exchange/confirm`,
    data,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX
  });

/* ███████████████████████████ *\
 * ███ REPRESENTATIVES-API ███ *
\* ███████████████████████████ */

export const getCandidateInfo = (email) =>
  api({
    path: '/account/info',
    data: { email }
  });

export const addRepresentativeRequest = ({ customerNumber, email, permissions }) =>
  api({
    path: `/account/representative/${customerNumber}/add`,
    data: {
      representative_email: email,
      permissions
    }
  });

export const addConfirmationRepresentativeRequest = ({ customerNumber, confirmationId, securityCode }) =>
  api({
    path: `/account/representative/${customerNumber}/add/confirm`,
    method: 'post',
    data: {
      confirmation_id: confirmationId,
      code: securityCode
    }
  });

export const removeRepresentativeRequest = ({ customerNumber, id }) =>
  api({
    path: `/account/representative/${customerNumber}/remove`,
    method: 'post',
    data: {
      representative_id: id
    }
  });

export const removeConfirmationRepresentativeRequest = ({ customerNumber, confirmationId, securityCode }) =>
  api({
    path: `/account/representative/${customerNumber}/remove/confirm`,
    method: 'post',
    data: {
      confirmation_id: confirmationId,
      code: securityCode
    }
  });

export const updateRepresentativeRequest = ({ customerNumber, id, permissions }) =>
  api({
    path: `/account/representative/${customerNumber}/update`,
    method: 'post',
    data: {
      representative_id: id,
      permissions
    }
  });

export const updateConfirmationRepresentativeRequest = ({ customerNumber, confirmationId, securityCode }) =>
  api({
    path: `/account/representative/${customerNumber}/update/confirm`,
    method: 'post',
    data: {
      confirmation_id: confirmationId,
      code: securityCode
    }
  });

export const updateRepresentative = ({ customerNumber, id, permissions, securityCode }) =>
  api({
    path: `/account/representative/${customerNumber}/update/${id}`,
    data: permissions,
    securityCode: securityCode
  });

/* █████████████████████████████ *\
 * ███ ACTIVITY-LOG-API ███ *
\* █████████████████████████████ */

export const getLoginAttempts = async (filter) =>
  api({
    path: '/login_attempt',
    method: 'get',
    pathPrefix: REACT_APP_API_URL_PATH_PREFIX,
    query: filter
  });

/* ████████████████████████████ *\
 * ███ Messenger Controller ███ *
\* ████████████████████████████ */

export const getMessageList = async (customerNumber, filter) => {
  return await api({
    pathPrefix: REACT_APP_MESSAGER_API_URL_PATH_PREFIX,
    path: `/account/${customerNumber}/topic/list`,
    method: 'get',
    query: filter
  });
};

export const markTopicAsReadRequest = async (accountNumber, topicId) => {
  return await api({
    pathPrefix: REACT_APP_MESSAGER_API_URL_PATH_PREFIX,
    path: `/account/${accountNumber}/topic/${topicId}/read`,
    method: 'get'
  });
};

export const getTopicMessages = async (customerNumber, topicId, query = {}) => {
  return await api({
    pathPrefix: REACT_APP_MESSAGER_API_URL_PATH_PREFIX,
    path: `/account/${customerNumber}/topic/${topicId}/message/list`,
    method: 'get',
    query
  });
};

export const createMessage = async (customerNumber, topicId, data) => {
  return await api({
    pathPrefix: REACT_APP_MESSAGER_API_URL_PATH_PREFIX,
    path: `/account/${customerNumber}/topic/${topicId}/message/create`,
    data
  });
};

export const confirmMessageCreation = async (customerNumber, topicId, data) => {
  return await api({
    pathPrefix: REACT_APP_MESSAGER_API_URL_PATH_PREFIX,
    path: `/account/${customerNumber}/topic/${topicId}/message/create/confirm`,
    method: 'post',
    data
  });
};

export const createTopic = async (customerNumber, data) => {
  return await api({
    pathPrefix: REACT_APP_MESSAGER_API_URL_PATH_PREFIX,
    path: `/account/${customerNumber}/topic/create`,
    method: 'post',
    data
  });
};

export const confirmTopicCreation = async (customerNumber, data) => {
  return await api({
    pathPrefix: REACT_APP_MESSAGER_API_URL_PATH_PREFIX,
    path: `/account/${customerNumber}/topic/create/confirm`,
    method: 'post',
    data
  });
};

export const downloadMessageDocument = async (customerNumber, documentId, isPreview) => {
  return await api({
    path: `/account/${customerNumber}/document/${documentId}`,
    pathPrefix: REACT_APP_MESSAGER_API_URL_PATH_PREFIX,
    method: 'get',
    responseType: 'blob',
    returnFullResponse: true
  }).then((res) => {
    if (isPreview) {
      return downloadFile(res, isPreview);
    } else {
      downloadFile(res);
    }
  });
};

export const uploadMessageDocument = (customerNumber, file) => {
  return api({
    path: `/account/${customerNumber}/document`,
    pathPrefix: REACT_APP_MESSAGER_API_URL_PATH_PREFIX,
    data: file
  });
};

/* █████████████████*\
 * ███ CARDS-API ███ *
\* █████████████████ */

export const getCardsList = async (customerNumber) => {
  return await api({
    path: `/account/${customerNumber}/card/list`,
    method: 'get',
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX
  });
};

export const createCardRequest = async (customerNumber, data) => {
  const res = await api({
    path: `/account/${customerNumber}/card/create`,
    data,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX
  });

  return { ...res };
};

export const confirmCardCreation = async (customerNumber, data) =>
  await api({
    path: `/account/${customerNumber}/card/create/confirm`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    data
  });

export const getCardNumberRequest = async (customerNumber, card_id, data) =>
  await api({
    path: `/account/${customerNumber}/card/${card_id}/number`,
    data,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX
  });

export const getCardCVVRequest = async (customerNumber, card_id, data) =>
  await api({
    path: `/account/${customerNumber}/card/${card_id}/cvv`,
    data,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX
  });

export const getCardAuthorizationsListRequest = async (customerNumber, filters = {}) =>
  await api({
    path: `/account/${customerNumber}/card/authorization/list`,
    method: 'get',
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    query: filters
  });

export const blockCardRequest = async (customerNumber, card_id) => {
  return await api({
    path: `/account/${customerNumber}/card/${card_id}/block`,
    method: 'get',
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX
  });
};

export const unblockCardRequest = async (customerNumber, card_id) => {
  return await api({
    path: `/account/${customerNumber}/card/${card_id}/unblock`,
    method: 'get',
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX
  });
};

export const replaceCardRequest = async (customerNumber, card_id) => {
  return await api({
    path: `/account/${customerNumber}/card/${card_id}/replace`,
    method: 'get',
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX
  });
};

/* █████████████████████████████████████ *\
 * █████ PREPARED-TRANSACTIONS-API █████ *
\* █████████████████████████████████████ */

export const getPreparedTransactionsList = async (customerNumber, filters = {}) => {
  return await api({
    path: `/account/${customerNumber}/transaction/prepared/list`,
    method: 'get',
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    query: filters
  });
};

export const createPreparedTransaction = async (customerNumber, data) => {
  return await api({
    method: 'post',
    path: `/account/${customerNumber}/transaction/prepared/create`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    data
  });
};

export const updatePreparedTransactionRequest = async (customerNumber, preparedTransactionId, data) => {
  return await api({
    method: 'post',
    path: `/account/${customerNumber}/transaction/prepared/${preparedTransactionId}/update`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    data
  });
};

export const createPreparedCryptoTransaction = async (customerNumber, data) => {
  return await api({
    method: 'post',
    path: `/account/${customerNumber}/transaction/prepared/crypto/create`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    data
  });
};

export const updatePreparedCryptoTransaction = async (customerNumber, preparedTransactionId, data) => {
  return await api({
    method: 'post',
    path: `/account/${customerNumber}/transaction/prepared/crypto/${preparedTransactionId}/update`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    data
  });
};

export const deletePreparedTransactions = async (customerNumber, preparedTransactionIds) => {
  return await api({
    method: 'post',
    path: `/account/${customerNumber}/transaction/prepared/delete`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    data: preparedTransactionIds
  });
};

export const submitPreparedTransactions = async (customerNumber, preparedTransactionIds) => {
  return await api({
    method: 'post',
    path: `/account/${customerNumber}/transaction/prepared/submit`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    data: preparedTransactionIds
  });
};

export const confirmPreparedTransactions = async (customerNumber, data) => {
  return await api({
    method: 'post',
    path: `/account/${customerNumber}/transaction/prepared/confirm`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    data
  });
};

export const calculatePaymentCommissionInBatchRequest = async (customerNumber, data) =>
  await api({
    path: `/account/${customerNumber}/transaction/commission/batch/calculate`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    data
  });

export const getPreparedTransactionDataForEditRequest = async (customerNumber, preparedTransactionId) => {
  return await api({
    method: 'get',
    path: `/account/${customerNumber}/transaction/prepared/${preparedTransactionId}/edit`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX
  });
};

/* ████████████████████████████████████ *\
 * ████ TEMPLATES-TRANSACTIONS-API ████ *
\* ████████████████████████████████████ */

export const validateTemplateAccountDetailsRequest = async (customerNumber, data) => {
  return await api({
    method: 'post',
    path: `/account/${customerNumber}/transaction/recipient/account-number/validate`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    data
  });
};

export const validateTemplateBankDetailsRequest = async (customerNumber, data) => {
  return await api({
    method: 'post',
    path: `/account/${customerNumber}/transaction/recipient/bank-details/validate`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    data
  });
};

export const validateTemplateRecipientDetailsRequest = async (customerNumber, data) => {
  return await api({
    method: 'post',
    path: `/account/${customerNumber}/transaction/recipient/validate`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    data
  });
};

export const validateTemplateAdditionalRecipientDetailsRequest = async (customerNumber, data) => {
  return await api({
    method: 'post',
    path: `/account/${customerNumber}/transaction/recipient/recipient-details/validate`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    data
  });
};

export const validateTemplatePaymentDetailsRequest = async (customerNumber, data) => {
  return await api({
    method: 'post',
    path: `/account/${customerNumber}/transaction/recipient/transaction-details/validate`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    data
  });
};

export const createTemplateRequest = async (customerNumber, data) => {
  return await api({
    method: 'post',
    path: `/account/${customerNumber}/transaction-template/create`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    data
  });
};

export const updateTemplateRequest = async (customerNumber, data, templateId) => {
  return await api({
    method: 'post',
    path: `/account/${customerNumber}/transaction-template/${templateId}/update`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    data
  });
};

export const deleteTemplateRequest = async (customerNumber, templateId) => {
  return await api({
    method: 'delete',
    path: `/account/${customerNumber}/transaction-template/${templateId}`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX
  });
};

export const getTemplatesListRequest = async (customerNumber, filters = {}) => {
  return await api({
    path: `/account/${customerNumber}/transaction-template/list`,
    method: 'get',
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    query: filters
  });
};

export const validateTemplatePaymentAmountRequest = async (customerNumber, templateId, data) => {
  return await api({
    method: 'post',
    path: `/account/${customerNumber}/transaction-template/${templateId}/validate-amount`,
    pathPrefix: REACT_APP_PAYMENT_API_URL_PATH_PREFIX,
    data
  });
};
